// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useTranslation } from "react-i18next";
import { useStoreDetailQuery } from "@/api/hooks/use-stores";
import { ChainLink } from "@/components/shared/chain-link";
import { DateTimeValue, DateValue } from "@/components/shared/datetime-value";
import { KeyChip } from "@/components/shared/key-chip";
import { TimezoneCell } from "@/components/shared/timezone-cell";
import { Separator } from "@/components/ui/separator";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";

// Store open/close + camera on/off are stored as `HH:MM:SS` time-of-day strings (not timestamps),
// so they're sliced to `HH:MM` rather than run through the Date-based time helper.
function formatTime(value: string | undefined) {
  if (!value) return "—";
  return value.slice(0, 5);
}

interface StoreInfoTabProps {
  storeName: string;
}

interface StoreDetail {
  storeName: string;
  legibleName: string;
  storeCode: string;
  chainName: string;
  goLiveDate?: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  address?: {
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
    region?: string;
  };
  details?: {
    timeZone?: string;
    openTime?: string;
    closeTime?: string;
  };
  cameras?: {
    deviceType?: string;
    count?: number;
    activeCount?: number;
    onTime?: string;
    offTime?: string;
  };
}

export function StoreInfoTab({ storeName }: StoreInfoTabProps) {
  const { t } = useTranslation("back-office");
  const { data: storeRaw } = useStoreDetailQuery(storeName);
  const store = storeRaw as unknown as StoreDetail | undefined;
  const { data: ctx } = useBackofficeContext();
  const chainDisplay = ctx?.chains?.find((c) => c.value === store?.chainName)?.display;

  if (!store) return null;

  const address = store.address;
  const details = store.details;
  const cameras = store.cameras;

  return (
    <div className="max-w-2xl space-y-6 p-6">
      <div className="space-y-4">
        <div className="grid grid-cols-[160px_1fr] gap-y-3 text-sm">
          <span className="text-muted-foreground">{t("stores.info.fields.displayName")}</span>
          <span>{store.legibleName}</span>

          <span className="text-muted-foreground">{t("stores.info.fields.storeName")}</span>
          <span>
            <KeyChip label={store.storeName} variant="store" />
          </span>

          <span className="text-muted-foreground">{t("stores.info.fields.storeCode")}</span>
          <span className="font-mono">{store.storeCode}</span>

          <span className="text-muted-foreground">{t("stores.info.fields.chain")}</span>
          <span>
            <ChainLink chainName={store.chainName} legibleName={chainDisplay} />
          </span>

          <span className="text-muted-foreground">{t("stores.info.fields.goLiveDate")}</span>
          <DateValue value={store.goLiveDate} className="text-sm" />

          <Separator className="col-span-2" />

          <span className="text-muted-foreground">{t("stores.info.fields.address")}</span>
          <span>{address?.address ?? "—"}</span>

          <span className="text-muted-foreground">{t("stores.info.fields.city")}</span>
          <span>{address?.city ?? "—"}</span>

          <span className="text-muted-foreground">{t("stores.info.fields.zipCode")}</span>
          <span className="tabular-nums">{address?.zipCode ?? "—"}</span>

          <span className="text-muted-foreground">{t("stores.info.fields.state")}</span>
          <span>{address?.state ?? "—"}</span>

          <span className="text-muted-foreground">{t("stores.info.fields.country")}</span>
          <span>{address?.country ?? "—"}</span>

          <span className="text-muted-foreground">{t("stores.info.fields.region")}</span>
          <span>{address?.region ?? "—"}</span>

          <Separator className="col-span-2" />

          <span className="text-muted-foreground">{t("stores.info.fields.timeZone")}</span>
          <span>
            {details?.timeZone ? (
              <TimezoneCell timeZone={details.timeZone} />
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </span>

          <span className="text-muted-foreground">{t("stores.info.fields.openTime")}</span>
          <span className="font-mono text-sm">{formatTime(details?.openTime)}</span>

          <span className="text-muted-foreground">{t("stores.info.fields.closeTime")}</span>
          <span className="font-mono text-sm">{formatTime(details?.closeTime)}</span>

          <Separator className="col-span-2" />

          <span className="text-muted-foreground">{t("stores.info.fields.deviceType")}</span>
          <span>{cameras?.deviceType ?? "—"}</span>

          <span className="text-muted-foreground">{t("stores.info.fields.cameras")}</span>
          <span className="tabular-nums">
            {cameras
              ? t("stores.info.cameraCountSummary", {
                  active: cameras.activeCount ?? 0,
                  total: cameras.count ?? 0,
                })
              : "—"}
          </span>

          <span className="text-muted-foreground">{t("stores.info.fields.cameraOnTime")}</span>
          <span className="font-mono text-sm">{formatTime(cameras?.onTime)}</span>

          <span className="text-muted-foreground">{t("stores.info.fields.cameraOffTime")}</span>
          <span className="font-mono text-sm">{formatTime(cameras?.offTime)}</span>

          <Separator className="col-span-2" />

          <span className="text-muted-foreground">{t("common.createdAt")}</span>
          <DateTimeValue value={store.createdAt} />

          <span className="text-muted-foreground">{t("common.updatedAt")}</span>
          <DateTimeValue value={store.updatedAt} />
        </div>
      </div>
    </div>
  );
}
